import { useQuery, useMutation, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import { peopleCoreAPI } from '@/shared/lib/api';
import { toast } from '@/shared/lib/toast';
import type { TimeEntry, Timesheet } from '../types';

export function useTimeEntry() {
  const queryClient = useQueryClient();

  const { data: todayEntry, isLoading: isLoadingToday } = useQuery<TimeEntry | null>({
    queryKey: ['people-core', 'time-entries', 'today'],
    queryFn: async () => {
      const res = await peopleCoreAPI.getTodayEntry();
      return res.data.data ?? null;
    },
    staleTime: 30 * 1000, // 30 segundos - ponto é tempo real
    refetchInterval: 60 * 1000, // atualiza a cada 1 min
  });

  const invalidateToday = () => {
    queryClient.invalidateQueries({ queryKey: ['people-core', 'time-entries', 'today'] });
    queryClient.invalidateQueries({ queryKey: ['people-core', 'timesheet'] });
  };

  const clockInMutation = useMutation({
    mutationFn: () => peopleCoreAPI.clockIn(),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Entrada registrada!');
      invalidateToday();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Erro ao registrar entrada');
    },
  });

  const breakStartMutation = useMutation({
    mutationFn: () => peopleCoreAPI.breakStart(),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Saída para almoço registrada!');
      invalidateToday();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Erro ao registrar saída para almoço');
    },
  });

  const breakEndMutation = useMutation({
    mutationFn: () => peopleCoreAPI.breakEnd(),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Retorno do almoço registrado!');
      invalidateToday();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Erro ao registrar retorno do almoço');
    },
  });

  const clockOutMutation = useMutation({
    mutationFn: () => peopleCoreAPI.clockOut(),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Saída registrada!');
      invalidateToday();
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Erro ao registrar saída');
    },
  });

  return {
    todayEntry,
    isLoadingToday,
    clockIn: clockInMutation.mutate,
    isClockingIn: clockInMutation.isPending,
    breakStart: breakStartMutation.mutate,
    isBreakStarting: breakStartMutation.isPending,
    breakEnd: breakEndMutation.mutate,
    isBreakEnding: breakEndMutation.isPending,
    clockOut: clockOutMutation.mutate,
    isClockingOut: clockOutMutation.isPending,
  };
}

export function useTimesheet(
  params?: { startDate?: string; endDate?: string; employeeId?: string },
  options?: Omit<UseQueryOptions<Timesheet[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<Timesheet[]>({
    queryKey: ['people-core', 'timesheet', params],
    queryFn: async () => {
      const res = await peopleCoreAPI.getTimesheet(params);
      return res.data.data ?? [];
    },
    ...options,
  });
}

export function useTimeEntries(
  params?: { startDate?: string; endDate?: string; employeeId?: string },
  options?: Omit<UseQueryOptions<TimeEntry[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery<TimeEntry[]>({
    queryKey: ['people-core', 'time-entries', params],
    queryFn: async () => {
      const res = await peopleCoreAPI.getTimeEntries(params);
      return res.data.data ?? [];
    },
    ...options,
  });
}
